import type { MouseEvent, MouseEventHandler, ReactNode } from "react";

import { LeaveFeedbackIcon, Profile02, SideMenuNavItem } from "@partners/ui";
import { Button, ICON_MAPPING, IconRenderer } from "@pepperstone/pepper-ui-kit";
import { Link } from "@tanstack/react-router";

import { LAUNCH_PERSONAL_TRADING_URL } from "@/components/Header";
import { getUrl } from "@/utils/domainResolver";

type NavItemProps = {
  label: string;
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  icon: ReactNode;
  href: string;
};
type LogoutNavItemProps = Omit<NavItemProps, "href" | "onClick"> & {
  onClick: MouseEventHandler<HTMLButtonElement>;
};

type ProfileMenuNavItemProps = Pick<NavItemProps, "label" | "onClick">;
type ProfileMenuLogoutNavItemProps = Pick<
  LogoutNavItemProps,
  "label" | "onClick"
>;

export const LAUNCH_MAM_URL = `${getUrl(import.meta.env.VITE_SCA_BASE_URL)}/mam`;
export const LAUNCH_WITHDRAW_URL = `${getUrl(import.meta.env.VITE_SCA_BASE_URL)}/wallet/withdrawal`;
export const LEAVE_FEEDBACK_URL =
  "https://pepperstonecore.qualtrics.com/jfe/form/SV_6gKfjTmpcApDFuC";
export const LAUNCH_TRANSFER_URL = `${getUrl(import.meta.env.VITE_SCA_BASE_URL)}/wallet/transfer`;
export const LAUNCH_PROFILE_URL = `${getUrl(import.meta.env.VITE_SCA_BASE_URL)}/my-profile`;

export const WithdrawNavItem = ({
  label,
  onClick,
}: ProfileMenuNavItemProps) => (
  <ProfileMenuExternalNavItem
    icon={<IconRenderer icon={ICON_MAPPING.withdraw} size="medium" />}
    label={label}
    onClick={onClick}
    href={LAUNCH_WITHDRAW_URL}
  />
);

export const TransferNavItem = ({
  label,
  onClick,
}: ProfileMenuNavItemProps) => (
  <ProfileMenuExternalNavItem
    icon={<IconRenderer icon={ICON_MAPPING["coins-swap"]} size="medium" />}
    label={label}
    onClick={onClick}
    href={LAUNCH_TRANSFER_URL}
  />
);

export const MAMNavItem = ({
  onClick,
}: Omit<ProfileMenuNavItemProps, "label">) => (
  <ProfileMenuExternalNavItem
    icon={<Profile02 />}
    label="MAM"
    onClick={onClick}
    href={LAUNCH_MAM_URL}
  />
);

export const TriageHistoryNavItem = ({
  label,
  onClick,
}: ProfileMenuNavItemProps) => (
  <ProfileMenuInternalNavItem
    icon={<IconRenderer icon={ICON_MAPPING.list} size="medium" />}
    label={label}
    onClick={onClick}
    href="/triage/history"
  />
);

export const TriageAddProgramNavItem = ({
  label,
  onClick,
  href,
}: ProfileMenuNavItemProps & { href: string }) => (
  <ProfileMenuInternalNavItem
    icon={<IconRenderer icon={ICON_MAPPING.plus} size="medium" />}
    label={label}
    onClick={onClick}
    href={href}
  />
);

export const LeaveFeedbackNavItem = ({
  label,
  onClick,
}: ProfileMenuNavItemProps) => (
  <ProfileMenuExternalNavItem
    icon={<LeaveFeedbackIcon />}
    label={label}
    onClick={onClick}
    href={LEAVE_FEEDBACK_URL}
  />
);

export const LogoutNavItem = ({
  label,
  onClick,
}: ProfileMenuLogoutNavItemProps) => (
  <ProfileMenuLogoutNavItem
    icon={<IconRenderer icon={ICON_MAPPING["log-out"]} size="medium" />}
    label={label}
    onClick={onClick}
  />
);

export const ProfileMenuLaunchPersonalTradingNavItem = ({
  label,
  onClick,
}: ProfileMenuNavItemProps) => (
  <Button
    as="a"
    type="button"
    variant="ps-btn-selected"
    theme="light"
    href={LAUNCH_PERSONAL_TRADING_URL}
    onClick={onClick}
    className="w-full"
  >
    {label}
  </Button>
);

const ProfileMenuLogoutNavItem = ({
  label,
  onClick,
  icon,
}: LogoutNavItemProps) => (
  <SideMenuNavItem
    icon={icon}
    label={label}
    isExternalLink={false}
    linkComponent={({ className, children }) => (
      <button className={className} type="button" onClick={onClick}>
        {children}
      </button>
    )}
  />
);

const ProfileMenuExternalNavItem = ({
  label,
  onClick,
  icon,
  href,
}: NavItemProps) => (
  <SideMenuNavItem
    icon={icon}
    label={label}
    isExternalLink={true}
    linkComponent={({ className, children }) => (
      <a className={className} href={href} target="_blank" onClick={onClick}>
        {children}
      </a>
    )}
  />
);

const ProfileMenuInternalNavItem = ({
  label,
  onClick,
  icon,
  href,
}: NavItemProps) => (
  <SideMenuNavItem
    icon={icon}
    label={label}
    isExternalLink={false}
    linkComponent={({ className, children }) => (
      <Link className={className} to={href} onClick={onClick}>
        {children}
      </Link>
    )}
  />
);
